import React, { useEffect, useCallback, useState } from 'react'
import Config from '../config'
import UploadService from '../services/UploadService'
import { noticeService, userService } from '../api'
import { useSelector } from 'react-redux'
import { find, first, sortBy } from 'lodash'

const EmailBuilder = ({ onClose, onConfirm, defaultConfiguration = null, defaultTemplateId = null }) => {
    const uploadService = new UploadService()
    const communityId = useSelector(state => state.navigation?.communityId)

    // Mock template data - replace with actual API calls
    const [templates, setTemplates] = useState([])

    const handleReset = () => {
        // Reset functionality can be implemented here
        console.log('Email content reset')
    }

    const handleConfirm = (data) => {
        onConfirm(data)
    }

    const handleDismiss = () => {
        onClose()
    }

    /**
     * Convert ArrayBuffer or base64 data back to File object
     * @param {Object} fileData - File data from iframe
     * @returns {File} - Reconstructed File object
     */
    const reconstructFile = (fileData) => {
        let fileContent
        
        if (fileData.data instanceof ArrayBuffer) {
            // Handle ArrayBuffer data
            fileContent = fileData.data
        } else if (typeof fileData.data === 'string' && fileData.data.startsWith('data:')) {
            // Handle base64 data URL
            const base64Data = fileData.data.split(',')[1]
            const byteCharacters = atob(base64Data)
            const byteNumbers = new Array(byteCharacters.length)
            
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i)
            }
            
            fileContent = new Uint8Array(byteNumbers)
        } else {
            throw new Error('Unsupported file data format')
        }
        
        return new File([fileContent], fileData.name, {
            type: fileData.type,
            lastModified: fileData.lastModified || Date.now()
        })
    }

    /**
     * Handle image upload requests from the iframe
     * @param {MessageEvent} event - Message event from iframe
     */
    const handleImageUploadRequest = async (event) => {
        const { requestId, templateId, file } = event.data
        
        console.log('Processing image upload request:', {
            requestId,
            templateId,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size
        })
        
        try {
            // Validate file data
            if (!file || !file.data) {
                throw new Error('No file data provided')
            }
            
            if (!file.name || !file.type) {
                throw new Error('Missing file metadata (name or type)')
            }
            
            // Validate file type (images only)
            if (!file.type.startsWith('image/')) {
                throw new Error('Only image files are supported')
            }
            
            // Validate file size (e.g., 10MB limit)
            const maxSize = 10 * 1024 * 1024 // 10MB
            if (file.size > maxSize) {
                throw new Error(`File size exceeds limit (${maxSize / 1024 / 1024}MB)`)
            }
            
            // Reconstruct File object from transferred data
            const fileObject = reconstructFile(file)
            
            console.log('Uploading file:', fileObject.name)
            
            // Upload using existing UploadService
            const uploadResult = await uploadService.upload({
                file: fileObject,
                type: 'image',
                mime: file.type,
                emailTemplateFileUpload: true
            })

            let finalUrl = uploadResult.url;

            // If we have a templateId, associate the file with the template immediately
            if (templateId && templateId !== 'new') {
                try {
                    const emailTemplateFileUploadResult = await noticeService.uploadFilesToEmailTemplate({
                        templateId: templateId,
                        relayUploadsFileIds: [uploadResult.id]
                    })

                    // Get the final URL from the email template file upload result
                    const uploadedFile = find(emailTemplateFileUploadResult.data.data, { relayUploadsFileId: uploadResult.id })
                    if (uploadedFile?.url) {
                        finalUrl = uploadedFile.url;
                    }
                    
                    console.log('Upload successful with template association:', uploadResult, emailTemplateFileUploadResult, uploadedFile)
                } catch (templateError) {
                    console.warn('Failed to associate with template, using relay upload URL:', templateError);
                }
            } else {
                console.log('Upload successful for new template (no association yet):', uploadResult)
            }
            
            // Send success response back to iframe
            event.source.postMessage({
                type: 'IMAGE_UPLOAD_RESPONSE',
                requestId,
                success: true,
                id: uploadResult.id,
                url: finalUrl
            }, '*')
            
        } catch (error) {
            console.error('Image upload failed:', error)
            
            // Send error response back to iframe
            event.source.postMessage({
                type: 'IMAGE_UPLOAD_RESPONSE',
                requestId,
                success: false,
                error: error.message || 'Upload failed'
            }, '*')
        }
    }

    // Handle template fetch requests
    const handleFetchTemplates = async (event) => {
        console.log('Fetching templates...')
        
        const response = await noticeService.getEmailTemplates({ communityId: communityId })
        setTemplates(sortBy(response.data.data, 'name'))
        
        // Send templates back to iframe
        event.source.postMessage({
            type: 'd6-portal:templates-response',
            templates: sortBy(response.data.data, 'name'),
            defaults: {
                templateId: defaultTemplateId,
                configuration: defaultConfiguration
            }
        }, '*')
    }

    // Handle template load requests (with files)
    const handleLoadTemplate = async (event) => {
        const { templateId } = event.data
        console.log('Loading template with files:', templateId)
        
        try {
            // Fetch template with files
            const response = await noticeService.getEmailTemplate({ templateId })
            const template = response.data
            
            // Send template and files back to iframe
            event.source.postMessage({
                type: 'd6-portal:template-loaded',
                template: template,
                files: template.files || []
            }, '*')
            
            // Also send to all frames to update file cache
            window.postMessage({
                type: 'd6-portal:template-saved',
                template: template
            }, '*')
            
        } catch (error) {
            console.error('Failed to load template:', error)
            
            // Send error response
            event.source.postMessage({
                type: 'd6-portal:template-load-error',
                error: error.message || 'Failed to load template',
                templateId: templateId
            }, '*')
        }
    }

    // Resolve fileIds in document configuration to final URLs
    const resolveFileIdsInDocument = (document, templateFiles) => {
        if (!document || typeof document !== 'object') {
            return document
        }

        if (Array.isArray(document)) {
            return document.map(item => resolveFileIdsInDocument(item, templateFiles))
        }

        const result = {}
        for (const [key, value] of Object.entries(document)) {
            if (key === 'fileId' && typeof value === 'string') {
                // Skip fileId properties - they're metadata, not for rendering
                continue
            } else if ((key === 'url' || key === 'imageUrl') && typeof value === 'string') {
                // Check if there's a corresponding fileId
                const fileId = document['fileId']
                if (fileId && templateFiles) {
                    // Find the file in template.files by matching the relay upload ID
                    const matchingFile = templateFiles.find(file => file.relayUploadsFileId === fileId)
                    if (matchingFile) {
                        // Replace blob URL with final URL
                        result[key] = matchingFile.url
                        console.log(`Resolved fileId ${fileId} to final URL: ${matchingFile.url}`)
                    } else {
                        result[key] = value
                    }
                } else {
                    result[key] = value
                }
            } else if (typeof value === 'object') {
                // Recursively process nested objects and arrays
                result[key] = resolveFileIdsInDocument(value, templateFiles)
            } else {
                result[key] = value
            }
        }
        
        return result
    }

    // Handle template save requests
    const handleSaveTemplate = async (event) => {
        const { template, newTemplateFiles, shouldNavigate } = event.data
        console.log('Saving template:', template, 'New template files:', newTemplateFiles)
        
        try {
            let savedTemplate
            
            if (template.id) {
                // Update existing template
                console.log('Updating existing template:', template.id)
                await noticeService.updateEmailTemplate({
                    templateId: template.id,
                    template: {
                        ...template,
                        community_id: communityId
                    }
                })
                savedTemplate = template
            } else {
                // Create new template
                console.log('Creating new template:', template.name)
                const response = await noticeService.createEmailTemplate({
                    template: {
                        ...template,
                        community_id: communityId
                    }
                })
                savedTemplate = response.data

                // If we have new template files, associate them with the newly created template
                if (newTemplateFiles && newTemplateFiles.length > 0) {
                    console.log('Associating files with new template:', savedTemplate.id, newTemplateFiles)
                    try {
                        const fileUploadResult = await noticeService.uploadFilesToEmailTemplate({
                            templateId: savedTemplate.id,
                            relayUploadsFileIds: newTemplateFiles
                        })
                        console.log('Files associated successfully:', fileUploadResult)
                        
                        // Update savedTemplate with files for response
                        savedTemplate.files = fileUploadResult.data.data || []
                        
                        // Resolve fileIds in template configuration to final URLs
                        if (savedTemplate.configuration && savedTemplate.files.length > 0) {
                            console.log('Resolving fileIds in template configuration for new template')
                            savedTemplate.configuration = resolveFileIdsInDocument(savedTemplate.configuration, savedTemplate.files)
                            
                            // Update the template with resolved configuration
                            try {
                                await noticeService.updateEmailTemplate({
                                    templateId: savedTemplate.id,
                                    template: {
                                        ...savedTemplate,
                                        community_id: communityId
                                    }
                                })
                                console.log('Template configuration updated with resolved URLs')
                            } catch (updateError) {
                                console.error('Failed to update template with resolved URLs:', updateError)
                                // Continue even if update fails
                            }
                        }
                    } catch (fileError) {
                        console.error('Failed to associate files with new template:', fileError)
                        // Continue with template save even if file association fails
                    }
                }
            }
            
            // Fetch fresh template list to ensure we have the latest data
            const templatesResponse = await noticeService.getEmailTemplates({ 
                communityId: communityId 
            })
            setTemplates(sortBy(templatesResponse.data.data, 'name'))
            
            // Fetch the complete template with files for response
            let completeTemplate = savedTemplate
            try {
                const templateWithFiles = await noticeService.getEmailTemplate({ 
                    templateId: savedTemplate.id 
                })
                completeTemplate = templateWithFiles.data
            } catch (fetchError) {
                console.error('Failed to fetch complete template:', fetchError)
                // Use savedTemplate as fallback
            }
            
            // Notify iframe that template was saved with fresh template list
            event.source.postMessage({
                type: shouldNavigate ? 'd6-portal:template-saved-and-navigated' : 'd6-portal:template-saved',
                template: completeTemplate,
                templates: sortBy(templatesResponse.data.data, 'name'), // Send fresh list
                files: completeTemplate.files || [], // Include files in response
                urlMapping: savedTemplate.files || [] // Send files for URL mapping
            }, '*')
            
            console.log('Template saved successfully:', savedTemplate.name)
            
        } catch (error) {
            console.error('Failed to save template:', error)
            
            // Notify iframe of error
            event.source.postMessage({
                type: 'd6-portal:template-save-error',
                error: error.message || 'Failed to save template'
            }, '*')
        }
    }

    // Handle template delete requests
    const handleDeleteTemplate = async (event) => {
        const { templateId } = event.data
        console.log('Deleting template:', templateId)
        
        try {
            // Delete template via API
            await noticeService.deleteEmailTemplate({
                communityId: communityId,
                templateId: templateId
            })
            
            // Fetch fresh template list to ensure we have the latest data
            const templatesResponse = await noticeService.getEmailTemplates({ 
                communityId: communityId 
            })
            setTemplates(sortBy(templatesResponse.data.data, 'name'))
            
            // Notify iframe that template was deleted with fresh template list
            event.source.postMessage({
                type: 'd6-portal:template-deleted',
                templateId: templateId,
                templates: sortBy(templatesResponse.data.data, 'name') // Send fresh list
            }, '*')
            
            console.log('Template deleted successfully:', templateId)
            
        } catch (error) {
            console.error('Failed to delete template:', error)
            
            // Notify iframe of error
            event.source.postMessage({
                type: 'd6-portal:template-delete-error',
                error: error.message || 'Failed to delete template',
                templateId: templateId
            }, '*')
        }
    }

    // Handle template file upload requests from iframe
    const handleTemplateFileUploadRequest = async (event) => {
        const { requestId, templateId, relayUploadsFileId } = event.data
        console.log('Template file upload request:', { templateId, relayUploadsFileId })
        
        try {
            const response = await noticeService.uploadFilesToEmailTemplate({
                templateId: templateId,
                relayUploadsFileIds: [relayUploadsFileId]
            })
            
            // Get the uploaded files information from response
            const uploadedFiles = response.data?.files || []
            
            // Send success response with updated file information
            event.source.postMessage({
                type: 'TEMPLATE_FILE_UPLOAD_RESPONSE',
                requestId: requestId,
                success: true,
                uploadedFiles: uploadedFiles
            }, '*')
            
            console.log('Template file uploaded successfully:', relayUploadsFileId, uploadedFiles)
            
        } catch (error) {
            console.error('Failed to upload file to template:', error)
            
            // Send error response
            event.source.postMessage({
                type: 'TEMPLATE_FILE_UPLOAD_RESPONSE',
                requestId: requestId,
                success: false,
                error: error.message || 'Failed to upload file to template'
            }, '*')
        }
    }

    const handleIframeMessage = useCallback((event) => {
        // Only handle messages from the email preview iframe
        // Note: Config.emailPreviewUrl might be a full URL, so we extract the origin
        const allowedOrigin = new URL(Config.emailPreviewUrl).origin
        if (event.origin !== allowedOrigin) {
            return
        }
        
        switch (event.data?.type) {
            case 'd6-portal:done':
                handleConfirm(event.data)
                break;
            case 'd6-portal:fetch-templates':
                handleFetchTemplates(event)
                break;
            case 'd6-portal:load-template':
                handleLoadTemplate(event)
                break;
            case 'd6-portal:save-template':
                handleSaveTemplate(event)
                break;
            case 'd6-portal:delete-template':
                handleDeleteTemplate(event)
                break;
            case 'd6-portal:reset':
                handleReset()
                break;
            case 'd6-portal:dismiss':
                handleDismiss()
                break;
            case 'IMAGE_UPLOAD_REQUEST':
                handleImageUploadRequest(event)
                break;
            case 'TEMPLATE_FILE_UPLOAD_REQUEST':
                handleTemplateFileUploadRequest(event)
                break;
            default:
                console.log('Unknown message type:', event.data?.type)
                break;
        }
    }, [])

    // Subscribe to iframe messages for the duration of the session
    useEffect(() => {
        window.addEventListener('message', handleIframeMessage)

        return () => {
            window.removeEventListener('message', handleIframeMessage)
        }
    }, [handleIframeMessage])

    return (
        <div style={{ 
            width: '100vw', 
            height: '100vh', 
            margin: 0, 
            padding: 0,
            overflow: 'hidden'
        }}>
            <iframe
                id='email-builder-iframe'
                src={`${Config.emailPreviewUrl}${defaultTemplateId ? '#template/'.concat(defaultTemplateId) : ''}`}
                title="Email Builder"
                style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    margin: 0,
                    padding: 0
                }}
                frameBorder="0"
            />
        </div>
    )
}

export default EmailBuilder
